/**
 * The open-redirect guard for a `returnTo` / `next` value that must stay on
 * the origin it is resolved against. Host-independent, so a broker consumer,
 * the broker itself, yominoki and browser code all apply the same rule; pure
 * TypeScript with no runtime-specific imports for the same reason as
 * ./domainAllowlist.ts.
 *
 * "Starts with one slash" is not enough. WHATWG URL parsing reads `\` as `/`
 * for http(s) and strips tab, CR and LF anywhere in the input, so `/\evil.com`
 * and `/<TAB>/evil.com` both resolve to `https://evil.com/`, and CR/LF must
 * never reach a `Location` header.
 *
 * The value is judged as given, after whatever decoding the caller's source
 * did (`URLSearchParams` and form parsing decode once). The URL parser does
 * not percent-decode a path, so `/%5Cevil.com` and `/%2F%2Fevil.com` stay
 * same-origin paths and are accepted; their decoded forms are refused.
 */

/** Longer than any path this workspace routes; bounds the Location it becomes. */
export const MAX_RETURN_PATH_LENGTH = 2048;

/** Only "did it stay on the origin" matters, so any origin will do. */
const PROBE_ORIGIN = "https://return-path.invalid";

/**
 * Whether `raw` is a same-origin absolute path: at most
 * `MAX_RETURN_PATH_LENGTH` long, a leading single `/` (never `//`), no `\`
 * anywhere, no control character or DEL, and resolving to the origin it is
 * resolved against.
 */
export function isSafeRelativePath(raw: string): boolean {
  if (raw.length > MAX_RETURN_PATH_LENGTH) return false;
  if (!raw.startsWith("/") || raw.startsWith("//")) return false;
  if (raw.includes("\\")) return false;
  for (let i = 0; i < raw.length; i++) {
    const code = raw.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return false;
  }
  // Implied by the rules above today; kept so a rule loosened later cannot
  // open a redirect without this failing too. Not `URL.canParse`: browser
  // code calls this, and Safari before 17 lacks it.
  try {
    return new URL(raw, PROBE_ORIGIN).origin === PROBE_ORIGIN;
  } catch {
    return false;
  }
}

/** `raw` when it passes `isSafeRelativePath`, otherwise `fallback`. */
export function safeReturnPath(
  raw: string | null | undefined,
  fallback = "/",
): string {
  return raw && isSafeRelativePath(raw) ? raw : fallback;
}
